/**
 * One row per group of the breakdown dimension the selector names: the
 * group's label, how many tracked accounts carry it, and the comments
 * analysed from their posts in the window.
 *
 * A value no account carries is left out, and a dimension where none has a
 * comment in the window says why in one line with a way to tag accounts,
 * rather than a row of dashes per value. No route returns a sentiment score
 * per breakdown group (`core`'s `read_breakdown` offers views and comments
 * only), so none is drawn.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { count } from '../../../chrome/measure/format';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { accountsPath } from '../../control/paths';
import { useBreakdown, type Window } from '../api/queries';
import type { Breakdown } from '../api/schemas';
import { BreakdownSelect } from '../shared/BreakdownSelect';
import { ds, dsPlural } from '../shared/messages';
import { st2 } from './messages';
import { commentsLabel } from './RegionCard';
import { SentimentCard } from './SentimentCard';

type Group = Breakdown['groups'][number];

/** The groups with a comment count to show. */
function measured(breakdown: Breakdown): (Group & { total: number })[] {
  return breakdown.groups.flatMap((group) =>
    group.account_count > 0 && group.total !== null ? [{ ...group, total: group.total }] : [],
  );
}

export function TagsView(props: { breakdown: Breakdown; campaignId: string }): ReactNode {
  const { breakdown } = props;
  const groups = measured(breakdown);
  if (groups.length === 0) {
    const tagged = breakdown.groups.some((group) => group.account_count > 0);
    return (
      <EmptyState
        title={st2(tagged ? 'tags.silent' : 'tags.untagged', { name: breakdown.dimension.name })}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to={accountsPath(props.campaignId)}>{st2('tags.tagAccounts')}</Link>
          </Button>
        }
      />
    );
  }
  return (
    <section aria-label={breakdown.dimension.name} className="flex flex-col gap-2">
      <h3 className="text-eyebrow text-muted-foreground uppercase">{breakdown.dimension.name}</h3>
      <ul className="flex flex-col gap-2">
        {groups.map((group) => (
          <li key={group.value_id} aria-label={group.name} className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Chip tone="outline">{group.name}</Chip>
              <span data-numeric className="text-caption text-muted-foreground truncate">
                {dsPlural('groups.accounts.other', group.account_count, {
                  n: count(group.account_count, getLocale()),
                })}
              </span>
            </div>
            <span data-numeric className="text-body-sm shrink-0 text-right">
              {commentsLabel(group.total)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TagsCard(props: {
  campaignId: string;
  tenantId: string;
  window: Window;
  dimensionId: string;
  onDimensionChange: (dimensionId: string) => void;
}): ReactNode {
  const dimensionId = props.dimensionId === '' ? null : props.dimensionId;
  const query = useBreakdown(props.campaignId, props.tenantId, {
    metric: 'comments',
    dimensionId,
    window: props.window,
  });
  // A dimension the reader picked keeps its card, so the picker stays reachable.
  if (dimensionId === null && query.status === 'success' && query.data.groups.length === 0) {
    return null;
  }
  return (
    <SentimentCard
      title={st2('tags.title')}
      aside={
        <BreakdownSelect
          campaignId={props.campaignId}
          value={dimensionId}
          onChange={props.onDimensionChange}
        />
      }
    >
      <QueryStates
        query={query}
        isEmpty={(data) => data.groups.length === 0}
        empty={<EmptyState title={ds('groups.empty')} />}
      >
        {(data) => <TagsView breakdown={data} campaignId={props.campaignId} />}
      </QueryStates>
    </SentimentCard>
  );
}
